import * as yup from "yup";
import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FaCircleNotch } from "react-icons/fa";
import { IoSaveOutline } from "react-icons/io5";

import Textbox from "../../../../../components/common/Textbox";
import Textarea from "../../../../../components/common/Textarea";
import Toaster from "../../../../../components/common/Toaster";
import ReactSelect from "../../../../../components/common/ReactSelect";

import journalService from "../../../../../services/journalService";

const schema = yup.object({
  id: yup.string(),
  number: yup.string().required("Please enter account chart number."),
  name: yup.string().required("Please enter account chart name."),
  type: yup.object().shape({
    label: yup.string().required("Please select type."),
    value: yup.string().required("Please select value."),
  }),
});

const AccountChartForm = ({
  data,
  isLoading,
  setLoading,
  setShowForm,
  onSuccess,
}) => {
  const [types, setTypes] = useState([]);

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  const onSubmit = async (info) => {
    try {
      setLoading(true);

      const item = { ...info };
      item["account_chart_type_id"] = info.type.value;
      item["type_id"] = info.type.value;
      item["type_name"] = info.type.label;

      const { data } = await journalService.saveAccountChartAsync(item);
      const { result } = data;
      item["id"] = result;

      Toaster.Success("Account Chart Saved Successfully!");

      onSuccess(item);
      setShowForm(false);
      setLoading(false);
    } catch (ex) {
      setLoading(false);
      Toaster.Error("Something went wrong.");
    }
  };

  const fetchTypes = useCallback(async (mounted) => {
    try {
      const { result } = await journalService.getAccountChartTypesAsync();

      const options = result.map((item) => {
        const option = {};
        option["label"] = item.name;
        option["value"] = item.id;

        return option;
      });

      if (mounted) setTypes(options);
    } catch (ex) {
      Toaster.Error("Something went wrong.");
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    fetchTypes(mounted);

    return () => {
      mounted = false;
    };
  }, [fetchTypes]);

  useEffect(() => {
    if (data) {
      setValue("id", data.id);
      setValue("number", data.number);
      setValue("name", data.name);
      setValue("description", data.description);
      setValue("type", { label: data.type_name, value: data.type_id });
    }
  }, [data, setValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-6">
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <Textbox
            id="name"
            name="name"
            type="text"
            title="Name"
            placeholder="e.g. Cash and cash equivalents"
            register={register("name")}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-cols-5 gap-6">
        <div className="col-span-3">
          <ReactSelect
            id="type-dropdown"
            name="type"
            title="Type"
            options={types}
            control={control}
            register={register("type")}
            errors={errors}
          />
        </div>
        <div className="col-span-2">
          <Textbox
            id="number"
            name="number"
            type="number"
            title="Number"
            placeholder="e.g. 100"
            register={register("number")}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <Textarea
            id="description-textarea"
            name="description"
            title="Description"
            placeholder="Say something..."
            register={register("description")}
            errors={errors}
            rows={4}
            optional
          />
        </div>
      </div>
      <div className="grid grid-2 pt-2">
        <div className="col-span-2">
          <button
            type="submit"
            className="button button-primary disabled:opacity-70 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading && <FaCircleNotch className="animate-spin mr-3" />}
            {!isLoading && <IoSaveOutline className="mr-3" />}
            Save Changes
          </button>
        </div>
      </div>
    </form>
  );
};

export default AccountChartForm;
